// src/services/admin/NotificationManager.js
import axiosInstance from "../../utils/axiosInstance"; // Use your authenticated instance
import config from "../../config";

const BASE_URL = `/notification`; // already under /api/admin via axiosInstance
const ADMIN_NOTIFICATIONS_URL = `/notifications`; // for admin notifications

const NotificationManager = {
  /**
   * Upload media image for notifications.
   */
  async uploadMedia(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axiosInstance.post(`${BASE_URL}/upload-media`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    } catch (err) {
      console.error("Error uploading notification media:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Send a broadcast notification to all users.
   */
  async sendBroadcast({ title, body, image = null, data = null }) {
    try {
      const msg = { recipientToken: null, title, body, image, data };
      const res = await axiosInstance.post(`${BASE_URL}/send`, msg);
      return res.data;
    } catch (err) {
      console.error("Error broadcasting notification:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Send a notification to a particular user.
   */
  async sendToUser({ userId, title, body, image = null, data = null }) {
    try {
      const req = { userId, title, body, image, data };
      const res = await axiosInstance.post(`${BASE_URL}/send-to-user`, req);
      return res.data;
    } catch (err) {
      console.error("Error sending to user:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
 * Send a broadcast notification to a topic.
 */
  async sendToTopic({ topic = "all", title, body, image = null, data = null }) {
    try {
      const req = { topic, title, body, image, data };
      const res = await axiosInstance.post(`${BASE_URL}/broadcast`, req);
      return res.data;
    } catch (err) {
      console.error("Error sending to topic:", err.response?.data || err.message);
      throw err;
    }
  },

  // ==================== ADMIN NOTIFICATIONS ====================

  /**
   * Get all admin notifications
   */
  async getAllAdminNotifications() {
    try {
      const res = await axiosInstance.get(ADMIN_NOTIFICATIONS_URL);
      return res.data;
    } catch (err) {
      console.error("Error fetching admin notifications:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Get unread admin notifications
   */
  async getUnreadAdminNotifications() {
    try {
      const res = await axiosInstance.get(`${ADMIN_NOTIFICATIONS_URL}/unread`);
      return res.data;
    } catch (err) {
      console.error("Error fetching unread notifications:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    try {
      const res = await axiosInstance.get(`${ADMIN_NOTIFICATIONS_URL}/unread/count`);
      return res.data.count;
    } catch (err) {
      console.error("Error fetching unread count:", err.response?.data || err.message);
      return 0;
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      const res = await axiosInstance.put(`${ADMIN_NOTIFICATIONS_URL}/${notificationId}/read`);
      return res.data;
    } catch (err) {
      console.error("Error marking notification as read:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const res = await axiosInstance.put(`${ADMIN_NOTIFICATIONS_URL}/read-all`);
      return res.data;
    } catch (err) {
      console.error("Error marking all as read:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId) {
    try {
      const res = await axiosInstance.delete(`${ADMIN_NOTIFICATIONS_URL}/${notificationId}`);
      return res.data;
    } catch (err) {
      console.error("Error deleting notification:", err.response?.data || err.message);
      throw err;
    }
  },

  /**
   * Get notifications by type
   */
  async getNotificationsByType(type) {
    try {
      const res = await axiosInstance.get(`${ADMIN_NOTIFICATIONS_URL}/type/${type}`);
      return res.data;
    } catch (err) {
      console.error("Error fetching notifications by type:", err.response?.data || err.message);
      throw err;
    }
  },
};

export default NotificationManager;
